import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Match } from '@/lib/matches';
import { stageLabel } from '@/lib/matches';
import { MatchCard } from './MatchCard';

interface MatchStageGroupProps {
  stage: string;
  matches: Match[];
  onClick?: (match: Match) => void;
  defaultOpen?: boolean;
  useUTC?: boolean;
  showGroupHeader?: boolean;
}

export function MatchStageGroup({ stage, matches, onClick, defaultOpen = true, useUTC, showGroupHeader = false }: MatchStageGroupProps) {
  const [open, setOpen] = useState(defaultOpen);

  if (matches.length === 0) return null;

  if (showGroupHeader) {
    return (
      <div>
        <div className="flex items-center gap-2 px-3 py-2 mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            {stage}
          </span>
          <span className="text-[11px] text-text-muted tabular-nums">
            {matches.length} match{matches.length !== 1 ? 'es' : ''}
          </span>
        </div>
        <div className="grid gap-2 grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4">
          {matches.map((m) => (
            <MatchCard key={m.id} match={m} onClick={onClick} useUTC={useUTC} showCompetition={false} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-bg-elevated">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text">
            {stageLabel(stage)}
          </span>
          <span className="text-[11px] text-text-muted tabular-nums">
            {matches.length} match{matches.length !== 1 ? 'es' : ''}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-3 grid gap-2 grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4">
          {matches.map((m) => (
            <MatchCard key={m.id} match={m} onClick={onClick} useUTC={useUTC} showCompetition={false} />
          ))}
        </div>
      )}
    </div>
  );
}
